// Invoices for Processing — Simplified view for section assignees

// Shows only invoices where the current user is assigned to a verification
// section on a linked milestone condition (directly or via group membership).
// Story 35: Simplified Navigation for Condition Section Assignees

// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// router
import { useNavigate, useParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextUser } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';

// mui
import {
	alpha,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	InputAdornment,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	CheckCircle as IconApproved,
	HourglassEmpty as IconPending,
	Cancel as IconRejected,
	Payment as IconPayment,
	Receipt as IconInvoice,
	Search as IconSearch,
	Visibility as IconView,
	Warning as IconWarning,
	Refresh as IconRefresh,
} from '@mui/icons-material';

// types
import { Invoice, InvoiceStatus } from '../../../../@types';

// services
import { fetchInvoices, FetchParams } from '../../../../services/api';

// design system
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { radius, shadows, spacing, typography } from '../../../../utils/designSystem';
import { formatDate } from '../../../../utils/dates';
import { GRADIENTS } from '../../../../data/colors';

// Status Config

const STATUS_CONFIG: Record<
	InvoiceStatus,
	{ color: string; icon: React.ReactNode; label: string }
> = {
	PENDING_REVIEW: {
		color: '#ed6c02',
		icon: <IconPending fontSize="small" />,
		label: 'Pending Review',
	},
	READY_FOR_PAYMENT: {
		color: '#2e7d32',
		icon: <IconApproved fontSize="small" />,
		label: 'Ready for Payment',
	},
	PAYMENT_IN_PROGRESS: {
		color: '#0288d1',
		icon: <IconPayment fontSize="small" />,
		label: 'Payment In Progress',
	},
	PAID: { color: '#2e7d32', icon: <IconApproved fontSize="small" />, label: 'Paid' },
	REJECTED: { color: '#d32f2f', icon: <IconRejected fontSize="small" />, label: 'Rejected' },
	ON_HOLD: { color: '#ed6c02', icon: <IconWarning fontSize="small" />, label: 'On Hold' },
	DRAFT: { color: '#757575', icon: <IconPending fontSize="small" />, label: 'Draft' },
	DEACTIVATED: { color: '#757575', icon: <IconRejected fontSize="small" />, label: 'Deactivated' },
};

const colors = GRADIENTS.payment;

// Component

type SortField = 'invoiceNumber' | 'supplierName' | 'amount' | 'dueDate' | 'status' | 'created';
type SortDir = 'asc' | 'desc';

export const InvoicesForProcessing = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id: projectIdParam } = useParams();
	const { user } = useContext(ContextUser);
	const { project } = useContext(ContextProject);
	const projectId = project?.id;

	// state
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [sortField, setSortField] = useState<SortField>('created');
	const [sortDir, setSortDir] = useState<SortDir>('desc');

	// auth tokens
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch assigned invoices
	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !user?.id || !projectId) return;

		setLoading(true);
		setError(null);

		try {
			const res = await fetchInvoices({ accessToken, idToken } as FetchParams, {
				project: projectId,
				assignedTo: user.id,
			});

			if (res.error) {
				setError(res.error.message);
			} else {
				setInvoices(res.data || []);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
		} finally {
			setLoading(false);
		}
	}, [accessToken, idToken, user?.id, projectId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// derived stats
	const stats = useMemo(() => {
		const pending = invoices.filter((i) => i.status === 'PENDING_REVIEW').length;
		const ready = invoices.filter((i) => i.status === 'READY_FOR_PAYMENT').length;
		const paid = invoices.filter((i) => i.status === 'PAID').length;
		return [
			{ label: 'Total Assigned', value: invoices.length, color: colors.icon },
			{ label: 'Pending Review', value: pending, color: '#ed6c02' },
			{ label: 'Ready for Payment', value: ready, color: '#2e7d32' },
			{ label: 'Paid', value: paid, color: '#16a34a' },
		];
	}, [invoices]);

	// client-side search filter
	const filtered = useMemo(() => {
		if (!search.trim()) return invoices;
		const q = search.toLowerCase();
		return invoices.filter(
			(inv) =>
				(inv.num || '').toLowerCase().includes(q) ||
				(typeof inv.supplier === 'object' && inv.supplier ? inv.supplier.name : '')
					.toLowerCase()
					.includes(q) ||
				(inv.description || '').toLowerCase().includes(q)
		);
	}, [invoices, search]);

	// sort
	const sorted = useMemo(() => {
		return [...filtered].sort((a, b) => {
			const dir = sortDir === 'asc' ? 1 : -1;
			switch (sortField) {
				case 'invoiceNumber':
					return dir * (a.num || '').localeCompare(b.num || '');
				case 'supplierName':
					return (
						dir *
						(typeof a.supplier === 'object' && a.supplier ? a.supplier.name : '').localeCompare(
							typeof b.supplier === 'object' && b.supplier ? b.supplier.name : ''
						)
					);
				case 'amount':
					return dir * ((a.amount ?? 0) - (b.amount ?? 0));
				case 'dueDate':
					return dir * (new Date(a.dateDue || 0).getTime() - new Date(b.dateDue || 0).getTime());
				case 'status':
					return dir * a.status.localeCompare(b.status);
				case 'created':
				default:
					return dir * (new Date(a.created).getTime() - new Date(b.created).getTime());
			}
		});
	}, [filtered, sortField, sortDir]);

	// handlers
	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			setSortDir('asc');
		}
	};

	const handleViewInvoice = (invoiceId: number) => {
		navigate(`/project/${projectIdParam}/spend-plans/invoice/${invoiceId}`);
	};

	// format helpers
	const formatCurrency = (amount: number, currency: string) =>
		new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

	// ==========================================================================

	return (
		<Box>
			{/* Header — matches SpendPlan listing page design */}
			<HeaderListingPage
				icon={IconInvoice}
					title={t('spendPlan.invoicesForProcessing.title')}
					subtitle={t('spendPlan.invoicesForProcessing.subtitle')}
				variant="payment"
				titleBadge={
					<Chip
						label={loading ? '...' : `${invoices.length} assigned`}
						size="small"
						sx={{
							bgcolor: alpha(colors.icon, 0.1),
							color: colors.icon,
							fontWeight: 600,
							borderRadius: radius.sm,
						}}
					/>
				}
				actions={
					<Tooltip title="Refresh">
						<IconButton onClick={fetchData} disabled={loading}>
							<IconRefresh />
						</IconButton>
					</Tooltip>
				}
				stats={!loading ? stats : undefined}
			/>

			{/* Content */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Loading state */}
				{loading && (
					<Box
						sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
						<CircularProgress size={40} sx={{ color: colors.icon }} />
						<Typography sx={{ ...typography.body, color: 'text.secondary' }}>
							Loading assigned invoices…
						</Typography>
					</Box>
				)}

				{/* Error state */}
				{!loading && error && (
					<Paper
						elevation={0}
						sx={{
							p: spacing.xl,
							textAlign: 'center',
							bgcolor: alpha('#d32f2f', 0.04),
							border: '1px solid',
							borderColor: alpha('#d32f2f', 0.15),
							borderRadius: radius.md,
						}}>
						<IconWarning sx={{ fontSize: 48, color: '#d32f2f', mb: 1 }} />
						<Typography sx={{ ...typography.bodyBold, color: '#d32f2f' }}>{error}</Typography>
					</Paper>
				)}

				{/* Search — visible when loaded */}
				{!loading && !error && (
					<>
						<Box sx={{ mb: spacing.md }}>
							<TextField
								fullWidth
								placeholder="Search by invoice number, vendor, or description…"
								size="small"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								sx={{
									maxWidth: 480,
									'& .MuiOutlinedInput-root': {
										borderRadius: radius.sm,
										fontSize: typography.body.fontSize,
									},
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<IconSearch fontSize="small" sx={{ color: 'text.secondary' }} />
										</InputAdornment>
									),
								}}
							/>
						</Box>

						{/* Empty state */}
						{sorted.length === 0 ? (
							<Paper
								elevation={0}
								sx={{
									py: spacing.xxxl,
									px: spacing.xl,
									textAlign: 'center',
									bgcolor: alpha(colors.icon, 0.03),
									border: '1px solid',
									borderColor: alpha(colors.icon, 0.12),
									borderRadius: radius.md,
								}}>
								<Box
									sx={{
										width: 72,
										height: 72,
										borderRadius: radius.lg,
										background: `linear-gradient(135deg, ${alpha(colors.icon, 0.12)}, ${alpha(colors.border, 0.06)})`,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										mx: 'auto',
										mb: 2.5,
									}}>
									<IconInvoice sx={{ fontSize: 36, color: colors.icon }} />
								</Box>
								<Typography sx={{ ...typography.h4, color: colors.text, mb: 1 }}>
									{invoices.length === 0
										? 'No invoices assigned to you'
										: 'No invoices match your search'}
								</Typography>
								<Typography
									sx={{ ...typography.body, color: 'text.secondary', maxWidth: 480, mx: 'auto' }}>
									{invoices.length === 0
										? 'When a PIU finance team member uploads an invoice against a milestone where you are a verification section assignee, it will appear here automatically.'
										: 'Try adjusting your search terms.'}
								</Typography>
							</Paper>
						) : (
							/* Table */
							<TableContainer
								component={Paper}
								elevation={0}
								sx={{
									border: '1px solid',
									borderColor: '#e5e7eb',
									borderRadius: radius.md,
									boxShadow: shadows.card,
								}}>
								<Table>
									<TableHead>
										<TableRow sx={{ bgcolor: alpha(colors.icon, 0.04) }}>
											{(
												[
													['invoiceNumber', 'Invoice #'],
													['supplierName', 'Vendor'],
													['amount', 'Amount'],
													['dueDate', 'Due Date'],
													['status', 'Status'],
													['created', 'Received'],
												] as [SortField, string][]
											).map(([field, label]) => (
												<TableCell
													key={field}
													sx={{
														...typography.label,
														fontSize: '0.95rem',
														py: 1.5,
														borderColor: alpha(colors.icon, 0.12),
													}}>
													<TableSortLabel
														active={sortField === field}
														direction={sortField === field ? sortDir : 'asc'}
														onClick={() => handleSort(field)}>
														{label}
													</TableSortLabel>
												</TableCell>
											))}
											<TableCell
												align="right"
												sx={{
													...typography.label,
													fontSize: '0.95rem',
													py: 1.5,
													borderColor: alpha(colors.icon, 0.12),
												}}>
												Actions
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{sorted.map((inv) => {
											const sc = STATUS_CONFIG[inv.status] || STATUS_CONFIG.PENDING_REVIEW;
											return (
												<TableRow
													key={inv.id}
													hover
													sx={{
														cursor: 'pointer',
														'&:hover': { bgcolor: alpha(colors.icon, 0.03) },
														'& td': { borderColor: '#f3f4f6' },
													}}
													onClick={() => handleViewInvoice(inv.id)}>
													<TableCell>
														<Typography sx={{ ...typography.bodyMedium, fontSize: '1rem' }}>
															{inv.num}
														</Typography>
														{inv.milestoneName && (
															<Typography variant="caption" color="text.secondary">
																{inv.milestoneName}
															</Typography>
														)}
													</TableCell>
													<TableCell>
														<Typography sx={{ fontSize: '1rem' }}>
															{typeof inv.supplier === 'object' && inv.supplier
																? inv.supplier.name
																: ''}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography sx={{ fontSize: '1rem', fontFeatureSettings: '"tnum"' }}>
															{formatCurrency(inv.amount ?? 0, inv.currency)}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography sx={{ fontSize: '1rem' }}>
															{formatDate({ date: inv.dateDue })}
														</Typography>
													</TableCell>
													<TableCell>
														<Chip
															icon={sc.icon as React.ReactElement}
															label={sc.label}
															size="small"
															sx={{
																bgcolor: alpha(sc.color, 0.1),
																color: sc.color,
																fontWeight: 600,
																borderRadius: radius.sm,
																'& .MuiChip-icon': { color: sc.color },
															}}
														/>
													</TableCell>
													<TableCell>
														<Typography sx={{ fontSize: '1rem' }}>
															{formatDate({ date: inv.created })}
														</Typography>
													</TableCell>
													<TableCell align="right">
														<Tooltip title="View invoice details">
															<IconButton
																size="small"
																onClick={(e) => {
																	e.stopPropagation();
																	handleViewInvoice(inv.id);
																}}
																sx={{
																	color: colors.icon,
																	'&:hover': { bgcolor: alpha(colors.icon, 0.1) },
																}}>
																<IconView fontSize="small" />
															</IconButton>
														</Tooltip>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</>
				)}
			</Box>
		</Box>
	);
};
